import type { App } from "vue";
import * as Sentry from "@sentry/vue";
import { CaptureConsole } from "@sentry/integrations";
import { createCacheKey } from "@/shared/helpers/cacheHelper";
import { CacheService } from "@/shared/services/CacheService";
import { ENV_KEYS, getEnvValue } from "@/shared/helpers/envHelper";
import { useMainStore } from "@/stores/useMainStore";
import type { Response } from "@/api";

export function useSentry(app: App<Element>) {
  const main = useMainStore();

  async function setupSentryAsync() {
    if (import.meta.env.MODE === "development") return;

    const proxyUrl = "https://5d92835e844ef3428c730547a4c8437f@sentry.ou.org/2";
    console.debug("sentry proxyUrl = " + proxyUrl);

    const loglevels = ["error", "warn", "info"];
    // We cannot use the api call here because Cognito is not initialized yet
    // Instead, we use the cache to determine if we should log debug messages
    // Cache is being created in MasterPage.vue
    // main.userLogin is rehydrated automatically by Pinia
    const debugCacheKey = createCacheKey(
      { userLogin: main.userLogin },
      "IsConsoleDebug"
    );
    if (main.userLogin && CacheService.has(debugCacheKey)) {
      const isConsoleDebug = CacheService.get(
        debugCacheKey,
        null as any
      ) as Response<boolean>;
      if (isConsoleDebug.success && isConsoleDebug.response) {
        loglevels.push("debug");
      }
    }

    Sentry.init({
      app,
      environment: window.location.hostname,
      release: `vue-${getEnvValue(ENV_KEYS.BuildVersion)}`,
      dsn: proxyUrl,
      tunnel: "/api/v2/Events/Capture",
      replaysSessionSampleRate: 0.1,
      replaysOnErrorSampleRate: 1.0,
      integrations: [
        new Sentry.BrowserTracing(),
        new CaptureConsole({ levels: loglevels }),
        new Sentry.Replay(),
      ],
      tracesSampleRate: 0.2,
    });
  }

  return { setupSentryAsync };
}
